import {
	confirmedCancellationNoShow,
	endTimeAllCases,
	hasOverlap,
	rangesOverlap,
	startTimeAllCases
} from "./realTimeUtils"
import { getChairPatients } from "../features/schedule/derivedAssignments"

/**
 * Returns a boolean[] aligned to `chairs`: true if the chair has no
 * overlapping patient inside [patientStart, patientEnd]. The patient being
 * edited is excluded via `excludePatientMRN` so its own row doesn't count
 * as a conflict.
 *
 * @param {{chairId: string}[]} chairs
 * @param {number} patientStart - start time in minutes
 * @param {number} patientEnd - end time in minutes
 * @param {object[]} patientSchedule
 * @param {string|number} excludePatientMRN
 * @param {boolean} sandbox
 * @param {boolean} isTomorrowMode
 * @returns {boolean[]}
 */
export const getChairAvailabilityForWindow = (
	chairs,
	patientStart,
	patientEnd,
	patientSchedule,
	excludePatientMRN,
	sandbox,
	isTomorrowMode
) =>
	chairs.map(chair => {
		const overlap = getChairPatients(patientSchedule, chair.chairId).some(other => {
			if (
				parseInt(other.patientMRN) === parseInt(excludePatientMRN) ||
				confirmedCancellationNoShow(other)
			) {
				return false
			}
			return rangesOverlap(
				[
					startTimeAllCases(other, sandbox, isTomorrowMode),
					endTimeAllCases(other, sandbox, isTomorrowMode)
				],
				[patientStart, patientEnd]
			)
		})
		return !overlap
	})

/**
 * Returns the list of eligible chairs for a given infusion type
 * @param {string} infusionType
 * @param {{infusionType: {chairEligability: {chairId: {eligible: boolean}}}}[]} acuityTable
 * @returns {number[] | null} an array of chair ids that are eligible for the infusion type
 * or null if no acuity table entry is found
 */
export const getEligibleChairsForInfusionType = (infusionType, acuityTable) => {
	const acuityTableEntry = acuityTable && acuityTable.find(entry => entry.name === infusionType || entry.appointmentType === infusionType)
	if (!acuityTableEntry) {
		console.warn(`No acuity table entry found for infusion type ${infusionType}`)
		return null
	}

	return acuityTableEntry.eligibleChairs
		.filter(entry => entry.eligible)
		.map(entry => entry.chairId)
}

/**
 * A small chair patient is a patient that has five or fewer eligible chairs
 *
 * @param {{patientId: number, infusionType: string}} patient
 * @param {{infusionType: {chairEligability: {chairId: {eligible: boolean}}}}[]} acuityTable
 * @returns
 */
export const isSmallChairPatient = (patient, acuityTable) => {
	const chairIds = getEligibleChairsForInfusionType(patient.infusionType, acuityTable)
	if (!chairIds) {
		return false
	}
	return chairIds.length <= 5
}

/**
 * Returns a list of overlapping patient ids on the chair
 * @returns {number[]}
 */
export const getOverlappingPatientsOnChair = (chair, newPatient, schedule, sandbox, isTomorrowMode) => {
	return getChairPatients(schedule.patientSchedule, chair.chairId)
		.filter(p => !confirmedCancellationNoShow(p))
		.filter(p => hasOverlap(p, newPatient, sandbox, isTomorrowMode))
		.map(p => p.patientId)
}

/**
 * Checks if any active patients on a chair overlap with each other in time
 * @returns {boolean}
 */
export const doPatientsOverlapOnChair = (chair, patientSchedule, sandbox, isTomorrowMode) => {
	if (!chair || !patientSchedule) return false

	const patients = getChairPatients(patientSchedule, chair.chairId).filter(
		p => !confirmedCancellationNoShow(p)
	)

	for (let i = 0; i < patients.length; i++) {
		for (let j = i + 1; j < patients.length; j++) {
			if (hasOverlap(patients[i], patients[j], sandbox, isTomorrowMode)) return true
		}
	}
	return false
}

/**
 * Checks if a chair is eligible for a patient
 * @param {{chairId: number}} chair
 * @param {{patientId: number, infusionType: string}} newPatient
 * @param {{infusionType: {chairEligability: {chairId: {eligible: boolean}}}}[]} acuityTable
 * @returns {boolean} true if the chair is eligible for the patient, false otherwise
 */

export const isChairEligibleForPatient = (chair, newPatient, acuityTable) => {
	const infusionType = newPatient.infusionType

	if (!acuityTable) {
		console.warn(`No acuity table found`)
		return true
	}

	const acuityTableEntry = acuityTable.find(entry => entry.name === infusionType || entry.appointmentType === infusionType)
	if (!acuityTableEntry) {
		console.warn(`No acuity table entry found for infusion type ${infusionType}`)
		return true
	}

	const chairEligability = acuityTableEntry.eligibleChairs.find(
		entry => entry.chairId === chair.chairId
	)
	if (!chairEligability) {
		console.warn(`No chair eligability found for chair ${chair.chairId}`)
		return true
	}

	return chairEligability.eligible
}

/**
 * Finds the list of chair ids that are available for a new patient

 *
 * Checks for overlapping patients and for chair eligability rules
 * @param {{chairs: {chairId: number}[], patientSchedule: object[]}} schedule
 * @param {{patientId: number, infusionType: string}} newPatient
 * @param {{infusionType: {chairEligability: {chairId: {eligible: boolean}}}}[]} acuityTable
 * 
 * @returns {number[]} an array of chair ids that are available for the new patient
 */
export const getChairAvailabilityForPatient = (schedule, newPatient, acuityTable, sandbox, isTomorrowMode) => {
	const chairIds = []
	for (const chair of schedule.chairs) {
		if (!isChairEligibleForPatient(chair, newPatient, acuityTable)) {
			continue
		}

		const overlappingPatients = getOverlappingPatientsOnChair(chair, newPatient, schedule, sandbox, isTomorrowMode)
		if (overlappingPatients.length > 0) {
			continue
		}

		chairIds.push(chair.chairId)
	}
	return chairIds
}

export const getChairType = (chair) => {
	return chair.chairType
		? Object.keys(chair.chairType).find(
				type => chair.chairType[type]
			) || "Unknown Type"
		: "Unknown Type"
}